import React, { useEffect, useState } from "react";
import { Button, Form, Input, message } from "antd";
import {
  FormItemRequiredRule,
  UserPasswordRequiredRule,
  emailPattern,
} from "Utility/Constants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import gmail from "Assets/SocialMediaIconSvg/new-gmail.svg";
import AuthRightContent from "@/components/AuthRightContent";

export const ResetPassword = () => {
  const [form] = Form.useForm();
  const [isLoading, setisLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState("email"); // "email" or "reset"

  // Get code and email from URL if present
  useEffect(() => {
    const code = location?.search?.split("code=")?.[1]?.split("+email=")?.[0];
    let email = location?.search?.split("email=")?.[1];
    if (code) {
      form.setFieldsValue({ code });
      setCurrentStep("reset");
    }
    if (email) {
      form.setFieldsValue({ email });
    }
  }, [location, form]);

  // Handle sending reset code
  const handleSendCode = async (data) => {
    setisLoading(true);
    try {
      await Auth.forgotPassword(data.email);
      message.success("Verification code has been sent to your email");
      setCurrentStep("reset");
      form.setFieldsValue({ email: data.email }); // Preserve email for next step
    } catch (err) {
      if (err?.message === "Username/client id combination not found.") {
        message.error(
          "This email is not registered. Please check and try again."
        );

        setCurrentStep("email");
      } else if (
        err?.message ===
        "Cannot reset password for the user as there is no registered/verified email or phone_number"
      ) {
        message.error("Your email is not verified. Please contact support.", 5);
      } else {
        console.log(err, "err");
        message.error(err?.message);
      }
    } finally {
      setisLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (data) => {
    setisLoading(true);
    try {
      await Auth.forgotPasswordSubmit(data.email, data.code, data.password);
      message.success("Your password has been successfully reset");
      navigate("/login");
    } catch (err) {
      message.error(err?.message);
    } finally {
      setisLoading(false);
    }
  };

  const EmailStep = () => (
    <>
      <div>
        <h1
          className="font-bold text-gray-900 leading-tight"
          style={{ fontSize: "32px" }}
        >
          Reset your password
        </h1>
        <p className="text-lg text-gray-600 mt-2">
          Enter your email and we'll send you a verification code
        </p>
      </div>

      <div className="space-y-12">
        <Form
          onFinish={handleSendCode}
          layout="vertical"
          requiredMark={false}
          form={form}
          name="forgot-password"
          className="space-y-0"
        >
          <Form.Item
            name="email"
            rules={[
              FormItemRequiredRule,
              {
                pattern: emailPattern,
                message: "Please enter a valid email",
              },
            ]}
          >
            <Input
              placeholder="Email address"
              className="py-3 px-4 input-border"
              size="large"
            />
          </Form.Item>

          <Button
            htmlType="submit"
            type="primary"
            size="large"
            loading={isLoading}
            className="w-full h-12 text-base font-semibold rounded-lg bg-primary"
          >
            Send Reset Code
          </Button>
        </Form>

        <div className="text-start">
          <Link to="/login" className="text-sm text-gray-600 hover:underline">
            Remember your password?{" "}
            <span className="text-primary">Sign in</span>
          </Link>
        </div>
      </div>
    </>
  );

  const ResetStep = () => (
    <>
      <div>
        <h1
          className="font-bold text-gray-900 leading-tight"
          style={{ fontSize: "32px" }}
        >
          Create new password
        </h1>
        <p className="text-lg text-gray-600 mt-2">
          Enter the verification code and your new password
        </p>
      </div>

      <div className="space-y-12">
        <Form
          onFinish={handleResetPassword}
          layout="vertical"
          requiredMark={false}
          form={form}
          name="reset-password"
          className="space-y-4"
        >
          <Form.Item
            name="email"
            rules={[
              FormItemRequiredRule,
              {
                pattern: emailPattern,
                message: "Please enter a valid email",
              },
            ]}
            style={{ display: "none" }}
          >
            <Input />
          </Form.Item>

          {/* Gmail Button */}
          <div className="flex justify-start mb-4">
            <a
              href="https://mail.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors hover:text-black"
            >
              <img src={gmail} alt="Gmail" className="mr-2 w-7 h-7" />
              Open Gmail
            </a>
          </div>

          <Form.Item
            name="code"
            rules={[FormItemRequiredRule]}
            label="Verification Code"
          >
            <Input.OTP
              length={6}
              className="input-border py-3"
              size="large"
              inputClassName="py-3"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="New Password"
            rules={[FormItemRequiredRule, UserPasswordRequiredRule]}
          >
            <Input.Password
              placeholder="Enter your new password"
              className="py-3 px-4 input-border"
              size="large"
              iconRender={(visible) => (
                <FontAwesomeIcon
                  icon={visible ? faEye : faEyeSlash}
                  className="cursor-pointer"
                />
              )}
            />
          </Form.Item>

          <Form.Item
            name="repeat-password"
            label="Confirm Password"
            rules={[
              FormItemRequiredRule,
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords must match."));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Confirm your new password"
              className="py-3 px-4 input-border"
              size="large"
              iconRender={(visible) => (
                <FontAwesomeIcon
                  icon={visible ? faEye : faEyeSlash}
                  className="cursor-pointer"
                />
              )}
            />
          </Form.Item>

          <Button
            htmlType="submit"
            type="primary"
            size="large"
            loading={isLoading}
            className="w-full h-12 text-base font-semibold rounded-lg bg-primary"
          >
            Reset Password
          </Button>
        </Form>

        <div className="text-start">
          <Link to="/login" className="text-sm text-gray-600 hover:underline">
            Remember your password?{" "}
            <span className="text-primary">Sign in</span>
          </Link>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Content */}
          <div className="space-y-14">
            {currentStep === "email" ? <EmailStep /> : <ResetStep />}
          </div>

          {/* Right side content */}
          <AuthRightContent />
        </div>
      </div>
    </div>
  );
};
